import React, { useState, useRef } from 'react';
import './Careers.css';
import { sendPerkProviderForm } from '../services/api';

function Careers() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    linkedin: '',
    message: '',
    position: ''
  });
  const [formStatus, setFormStatus] = useState(null); // null, 'sending', 'success', 'error'
  const [resumeFile, setResumeFile] = useState(null);
  const fileInputRef = useRef(null);
  
  const openPositions = [
    {
      id: 1,
      title: 'Certified Public Accountant',
      location: 'US, Raleigh',
      description: 'We are looking for a licensed CPA to join our team. The ideal candidate will have experience in tax preparation, financial reporting, and client advisory.',
      requirements: ['Active CPA license', '3+ years of accounting experience', 'Strong knowledge of US GAAP', 'Experience with accounting software']
    },
    {
      id: 2,
      title: 'Accountant',
      location: 'US, Raleigh',
      description: 'Join our accounting team to provide bookkeeping and financial services to our growing client base.',
      requirements: ['Bachelor\'s degree in Accounting or Finance', '2+ years of accounting experience', 'QuickBooks proficiency', 'Strong attention to detail']
    },
    {
      id: 3,
      title: 'Accountant',
      location: 'India, Chennai',
      description: 'We are expanding our team in Chennai and looking for skilled accountants to join our global operations.',
      requirements: ['Bachelor\'s degree in Accounting or Commerce', '2+ years of accounting experience', 'Knowledge of US accounting principles', 'Excellent English communication skills']
    },
    {
      id: 4,
      title: 'Accountant',
      location: 'India, Chennai',
      description: 'Join our growing team in Chennai to support clients with bookkeeping and financial reporting needs.',
      requirements: ['Bachelor\'s degree in Accounting or Commerce', '1+ years of accounting experience', 'Knowledge of US accounting principles', 'Strong analytical skills']
    },
    {
      id: 5,
      title: 'Customer Support Specialist',
      location: 'US, Raleigh',
      description: 'Help our clients navigate their financial needs and provide exceptional customer service as part of our support team.',
      requirements: ['Previous customer service experience', 'Basic understanding of accounting concepts', 'Excellent communication skills', 'Problem-solving abilities']
    },
    {
      id: 6,
      title: 'Equity & Fund Administration Manager',
      location: 'US, Nationwide (Remote)',
      description: 'Lead our equity management and fund administration services for clients across various stages of growth.',
      requirements: ['5+ years of experience in equity management', 'Knowledge of equity compensation plans', 'Experience with cap table management', 'Understanding of fund structures and operations']
    },
    {
      id: 7,
      title: 'Enrolled Agent',
      location: 'US, Raleigh',
      description: 'Join our tax practice team as an Enrolled Agent to provide comprehensive tax services and represent clients before the IRS.',
      requirements: ['Active Enrolled Agent license', '3+ years of tax preparation experience', 'Experience with IRS representation and tax resolution', 'Knowledge of federal and state tax regulations', 'Strong analytical and communication skills']
    }
  ];

  const handleOpenModal = (job) => {
    setSelectedJob(job);
    setFormData({
      ...formData,
      position: job.title + " - " + job.location
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedJob(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = [
        'application/pdf', 
        'application/msword', 
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'text/plain'
      ];
      
      if (allowedTypes.includes(file.type)) {
        setResumeFile(file);
      } else {
        alert('Please upload a valid file type (PDF, Word, PowerPoint, or TXT)');
        e.target.value = null;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    
    // Create FormData object for file upload
    const data = new FormData();
    
    // Append form fields
    Object.keys(formData).forEach(key => {
      data.append(key, formData[key]);
    });
    
    // Append resume file if exists
    if (resumeFile) {
      data.append('resume', resumeFile);
    }
    
    // Add metadata
    data.append('type', 'job_application');
    data.append('source', 'website_careers_page');
    
    try {
      // Using existing API function (note: may need to be updated to handle FormData)
      await sendPerkProviderForm(data);
      
      setFormStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        linkedin: '',
        message: '',
        position: selectedJob ? selectedJob.title + " - " + selectedJob.location : ''
      });
      setResumeFile(null);
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setFormStatus(null);
        if (modalOpen) {
          handleCloseModal();
        }
      }, 5000);
    } catch (error) {
      console.error('Error sending job application:', error);
      setFormStatus('error');
      
      // Reset status after 5 seconds
      setTimeout(() => {
        setFormStatus(null);
      }, 5000);
    }
  };

  const ApplicationForm = ({ isModal = false }) => (
    <form className="application-form" onSubmit={handleSubmit}>
      <div className="form-group">
        <label htmlFor={`name${isModal ? '-modal' : ''}`}>Full Name</label>
        <input 
          type="text" 
          id={`name${isModal ? '-modal' : ''}`} 
          name="name" 
          value={formData.name}
          onChange={handleChange}
          required 
        />
      </div>
      <div className="form-group">
        <label htmlFor={`email${isModal ? '-modal' : ''}`}>Email</label>
        <input 
          type="email" 
          id={`email${isModal ? '-modal' : ''}`} 
          name="email" 
          value={formData.email}
          onChange={handleChange}
          required 
        />
      </div>
      <div className="form-group">
        <label htmlFor={`phone${isModal ? '-modal' : ''}`}>Phone</label>
        <input 
          type="tel" 
          id={`phone${isModal ? '-modal' : ''}`} 
          name="phone" 
          value={formData.phone}
          onChange={handleChange}
          required 
        />
      </div>
      <div className="form-group">
        <label htmlFor={`linkedin${isModal ? '-modal' : ''}`}>LinkedIn Profile (optional)</label>
        <input 
          type="url" 
          id={`linkedin${isModal ? '-modal' : ''}`} 
          name="linkedin" 
          value={formData.linkedin}
          onChange={handleChange}
          placeholder="https://linkedin.com/in/your-profile"
        />
      </div>
      {!isModal && (
        <div className="form-group">
          <label htmlFor="position">Position of Interest</label>
          <select
            id="position"
            name="position"
            value={formData.position}
            onChange={handleChange}
            required
          >
            <option value="">Select a position</option>
            {openPositions.map(job => (
              <option key={job.id} value={`${job.title} - ${job.location}`}>
                {job.title} - {job.location}
              </option>
            ))}
            <option value="General Application">General Application</option>
          </select>
        </div>
      )}
      <div className="form-group">
        <label htmlFor={`message${isModal ? '-modal' : ''}`}>Why would you like to join our team?</label>
        <textarea 
          id={`message${isModal ? '-modal' : ''}`} 
          name="message" 
          rows="4" 
          value={formData.message}
          onChange={handleChange}
          required
        ></textarea>
      </div>
      <div className="form-group">
        <label htmlFor={`resume${isModal ? '-modal' : ''}`}>Resume/CV</label>
        <input 
          type="file" 
          id={`resume${isModal ? '-modal' : ''}`} 
          name="resume" 
          onChange={handleFileChange}
          ref={fileInputRef}
          accept=".pdf,.doc,.docx,.ppt,.pptx,.txt"
          required 
        />
        <small className="file-help-text">Accepted formats: PDF, Word, PowerPoint, TXT</small>
      </div>
      <button 
        type="submit" 
        className="btn btn-primary"
        disabled={formStatus === 'sending'}
      >
        {formStatus === 'sending' ? 'Sending...' : 'Submit Application'}
      </button>
      {formStatus === 'success' && (
        <div className="form-status success">
          <p>Your application has been submitted successfully!</p>
        </div>
      )}
      {formStatus === 'error' && (
        <div className="form-status error">
          <p>There was an error submitting your application. Please try again.</p>
        </div>
      )}
    </form>
  );

  return (
    <div className="page-container careers-page">
      {/* Hero Banner Section */}
      <section className="careers-hero-banner">
        <div className="hero-overlay"></div>
        <div className="hero-image-container">
          <img src="/images/careers.jpg" alt="Join our team" className="hero-image" />
        </div>
        <div className="container">
          <h1>Join Our Team</h1>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="careers-content">
        <div className="container">
          <h2>Why Work With Us?</h2>
          <p className="intro-description">
            At rkube, we're building the future of financial operations for modern businesses. 
            We're a team of passionate professionals who are dedicated to simplifying complex financial 
            processes and helping our clients succeed.
          </p>
          
          <div className="benefits-grid">
            <div className="benefit-item">
              <h3>Growth Opportunities</h3>
              <p>Develop your skills and advance your career with our structured learning paths and mentorship programs.</p>
            </div>
            <div className="benefit-item">
              <h3>Collaborative Culture</h3>
              <p>Work with talented colleagues in a supportive environment that values teamwork and innovation.</p>
            </div>
            <div className="benefit-item">
              <h3>Work-Life Balance</h3>
              <p>Enjoy flexible work arrangements and policies designed to help you maintain a healthy work-life balance.</p>
            </div>
            <div className="benefit-item">
              <h3>Competitive Benefits</h3>
              <p>We offer competitive compensation packages, health benefits, retirement plans, and more.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section className="open-positions-section">
        <div className="container">
          <h2>Current Openings</h2>
          <div className="job-cards-grid">
            {openPositions.map(job => (
              <div key={job.id} className="job-card" onClick={() => handleOpenModal(job)}>
                <h3>{job.title}</h3>
                <div className="job-location">{job.location}</div>
                <p className="job-description">{job.description}</p>
                <button className="btn btn-outline">Apply Now</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* General Application Section */}
      <section className="general-application-section">
        <div className="container">
          <h2>Don't See a Perfect Fit?</h2>
          <p className="general-application-intro">
            We're always looking for talented individuals to join our team. Submit a general application, 
            and we'll keep your information on file for future opportunities.
          </p>
          <div className="general-application-form-container">
            <ApplicationForm />
          </div>
        </div>
      </section>

      {/* Job Application Modal */}
      {modalOpen && selectedJob && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Apply for {selectedJob.title}</h3>
              <button className="modal-close" onClick={handleCloseModal}>×</button>
            </div>
            <div className="modal-body">
              <div className="job-details">
                <div className="job-location modal-job-location">{selectedJob.location}</div>
                <p className="job-description">{selectedJob.description}</p>
                <div className="job-requirements">
                  <h4>Requirements:</h4>
                  <ul>
                    {selectedJob.requirements.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              </div>
              <ApplicationForm isModal={true} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Careers; 